import { __t, applyTranslations } from 'utils/language';
import { BRAND, BRAND_FILTER_QUERY_PARAM } from 'utils/brands';

import type { GenericCompareFn, List, LocationWindow } from 'types';

applyTranslations(require('./i18n/list-util.i18n.json'));

const MY_CREATED_PROFILE_SMARTLIST_NAME = 'Creators I have added to Collabary';

export const CUSTOM_LIST_TYPE = 'custom';
export const SMART_LIST_TYPE = 'smart';
export const LIST_STATE_STORAGE_NAME = 'prevListState';
export const LIST_DASHBOARD_SORT_OPTIONS = {
  MOST_RECENT: {
    label: __t('list_util.most_recent'),
    value: 'updated_at.desc'
  },
  BRAND_NAME: {
    label: __t('list_util.brand_name_a_z'),
    value: 'brand_name.asc'
  },
  LIST_NAME_A_Z: {
    label: __t('list_util.list_name_a_z'),
    value: 'list_name.asc'
  }
};
const SORT = 'sort';
const SEARCH = 'search';
const TAB = 'tab';
const PAGE = 'page';
const NAME = 'name';
const FILTER = BRAND_FILTER_QUERY_PARAM;
const PREV = 'prev';
const PER_PAGE = 'per_page';
export const LIST_DASHBOARD_QUERY_PARAMS = {
  SEARCH, SORT, TAB, PAGE, FILTER, PREV, PER_PAGE
};

export const STATS = 'STATS';
export const CREATORS = 'CREATORS';
export const DEFAULT_SORT = 'followers.desc';
export const RELEVANCE_SORT = 'relevance.desc';

export const COLLECTION_SORT_LIST = {
  'followers.desc': __t('list_util.sort_followers_highest_first_label'),
  'followers.asc': __t('list_util.sort_followers_lowest_first_label'),
  'engagement.desc': __t('list_util.sort_engagement_highest_first_label'),
  'engagement.asc': __t('list_util.sort_engagement_lowest_first_label'),
  'age.asc': __t('list_util.sort_age_youngest_first_label'),
  'age.desc': __t('list_util.sort_age_oldest_first_label'),
  'joined.desc': __t('list_util.sort_recently_joined_newest_first_label'),
  'joined.asc': __t('list_util.sort_recently_joined_oldest_first_label')
};

export type ListDashboardTabs = typeof CUSTOM_LIST_TYPE | typeof SMART_LIST_TYPE;

const defaultComparator = <T>(a: T, b: T): boolean => a === b;

export function indexOf<T>(list: Array<T>, item: T, compare: GenericCompareFn = defaultComparator): number {
  let index = -1;
  list.some((itemInList, i) => {
    if (compare(itemInList, item)) {
      index = i;
      return true;
    }
    return false;
  });

  return index;
}

export function addUniqueItem<T>(list: Array<T>, item: T, compare: GenericCompareFn = defaultComparator): Array<T> {
  if (!item) {
    return list;
  }

  if (typeof item === 'string' && !item.trim()) {
    return list;
  }

  if (indexOf(list, item, compare) >= 0) {
    return list;
  }

  list.push(item);
  return list;
}

function removeItem<T>(list: Array<T>, index: number): Array<T> {
  return list.slice(0, index)
    .concat(list.slice(index + 1));
}

export function findAndRemoveItem<T>(
  list: Array<T> = [],
  item: T | undefined | null = null,
  compare: GenericCompareFn = defaultComparator
): Array<T> {
  if (!list.length) {
    return list;
  }

  if (!item) {
    return list;
  }

  const index = indexOf(list, item, compare);

  if (index === -1) {
    return list;
  }

  return removeItem(list, index);
}

export function moveItem<T>(
  list: Array<T>,
  item: T,
  destinationIndex: number,
  compareFn: (b: T, a: T) => boolean = defaultComparator
): Array<T> {
  const newList = [...list];
  const sourceIndex = indexOf(list, item, compareFn);
  newList.splice(sourceIndex, 1);
  newList.splice(destinationIndex, 0, item);
  return newList;
}

export function insertItem<T>(list: Array<T>, item: T, index: number): Array<T> {
  return list.slice(0, index)
    .concat(item)
    .concat(list.slice(index));
}

export function splitInRows<T>(items: Array<T>, itemsPerRow: number): Array<Array<T>> {
  const rowCount = items.length ? Math.ceil(items.length / itemsPerRow) : 1;
  const grid: Array<Array<T>> = [];
  for (let i = 0; i < rowCount; i++) {
    const start = i * itemsPerRow;
    const end = (i + 1) * itemsPerRow;
    grid.push(items.slice(start, end));
  }
  return grid;
}

export function sortOldestFirst<T extends {id: number}>(
  list: Array<T>
): Array<T> {
  return (list || []).sort((itemA, itemB) => itemA.id - itemB.id);
}

// TODO: replace * with proper types (possibly generics), requires massive refactoring
export function insertIf(condition: any, ...elements: Array<any>): Array<any> {
  return condition ? elements : [];
}

export const getManageListStatus = (orgId: number, organizationsListIds: Array<number>): boolean => !(organizationsListIds.indexOf(orgId) === -1);

export const getQueryValueFromUrl = (location: LocationWindow, type: string): string => {
  const query = location.query || {};

  const values = {
    [PAGE]: query[PAGE] || '1',
    [NAME]: query[NAME] || '',
    [TAB]: query[TAB] || CUSTOM_LIST_TYPE,
    [FILTER]: query[FILTER],
    [PREV]: query[PREV] || '',
    [SEARCH]: query[SEARCH] || '',
    [SORT]: query[SORT] || LIST_DASHBOARD_SORT_OPTIONS.MOST_RECENT.value,
    [PER_PAGE]: query[PER_PAGE] || '10'
  };

  return values[type];
};
export const isCustomList = (list: List) => list.type === CUSTOM_LIST_TYPE;

export const isSmartList = (list: List) => list.type === SMART_LIST_TYPE;
export const isMyCreatedProfileSmartlist = (list: List) => list.label === MY_CREATED_PROFILE_SMARTLIST_NAME;

export const getSortOptions = (currentTab: ListDashboardTabs, currentBrandFilter: string) => {
  const { MOST_RECENT, BRAND_NAME, LIST_NAME_A_Z } = LIST_DASHBOARD_SORT_OPTIONS;
  if (currentTab === SMART_LIST_TYPE) {
    if (currentBrandFilter === BRAND) {
      return [MOST_RECENT];
    }
    return [MOST_RECENT, BRAND_NAME];
  }
  if (currentBrandFilter === BRAND) {
    return [MOST_RECENT, LIST_NAME_A_Z];
  }
  return [MOST_RECENT, BRAND_NAME, LIST_NAME_A_Z];
};
